import { PhoneIncoming, PhoneOutgoing, Loader2, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatPhoneDisplay } from '@/lib/utils'

const TZ_LABELS: Record<string, string> = {
  'America/New_York': 'Eastern',
  'America/Chicago': 'Central',
  'America/Denver': 'Mountain',
  'America/Los_Angeles': 'Pacific',
  'America/Phoenix': 'Arizona',
  'America/Anchorage': 'Alaska',
  'Pacific/Honolulu': 'Hawaii',
}

interface BuilderPhaseReviewProps {
  businessName: string
  practiceAreas: string[]
  selectedVoiceId: string
  tonePreset: string
  agentName: string
  greeting: string
  selectedPhoneId: string
  phoneNumbers: Array<{ id: string; phoneNumber: string }>
  creating: boolean
  error: string | null
  onCreate: () => void
  // Operations fields
  firmTimezone?: string
  businessHours?: string
  bookingUrl?: string
  additionalLanguages?: string[]
}

export default function BuilderPhaseReview({
  businessName,
  practiceAreas,
  agentName,
  greeting,
  selectedPhoneId,
  phoneNumbers,
  creating,
  error,
  onCreate,
  firmTimezone,
  businessHours,
  bookingUrl,
  additionalLanguages,
}: BuilderPhaseReviewProps) {
  const phone = phoneNumbers.find(p => p.id === selectedPhoneId)

  return (
    <div className="space-y-5">
      <div className="text-center space-y-1">
        <Sparkles className="h-6 w-6 text-brand-royal mx-auto" />
        <h3 className="text-lg font-semibold">Ready to create your AI team</h3>
        <p className="text-sm text-muted-foreground">We'll build two agents tailored to your firm.</p>
      </div>

      {/* Summary */}
      <div className="rounded-lg border p-4 space-y-3">
        <div className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-2 text-sm">
          <span className="text-muted-foreground">Firm</span>
          <span className="font-medium">{businessName}</span>
          <span className="text-muted-foreground">Practice areas</span>
          <span>{practiceAreas.length} area{practiceAreas.length !== 1 ? 's' : ''}</span>
          <span className="text-muted-foreground">Voice</span>
          <span>Molly</span>
          <span className="text-muted-foreground">AI name</span>
          <span>{agentName}</span>
          {firmTimezone && (
            <>
              <span className="text-muted-foreground">Timezone</span>
              <span>{TZ_LABELS[firmTimezone] || firmTimezone}</span>
            </>
          )}
          {businessHours && (
            <>
              <span className="text-muted-foreground">Hours</span>
              <span>{businessHours}</span>
            </>
          )}
          {bookingUrl && (
            <>
              <span className="text-muted-foreground">Booking</span>
              <span className="text-xs truncate">{bookingUrl}</span>
            </>
          )}
          {additionalLanguages && additionalLanguages.length > 0 && (
            <>
              <span className="text-muted-foreground">Languages</span>
              <span>English + {additionalLanguages.join(', ')}</span>
            </>
          )}
          {phone && (
            <>
              <span className="text-muted-foreground">Phone</span>
              <span className="font-mono text-xs">{formatPhoneDisplay(phone.phoneNumber)}</span>
            </>
          )}
        </div>
      </div>

      {/* Agent preview cards */}
      <div className="grid grid-cols-1 gap-3">
        <div className="rounded-lg border p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <PhoneIncoming className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium">{businessName} — Receptionist</p>
              <Badge variant="secondary" className="text-[10px]">Inbound</Badge>
            </div>
          </div>
          <p className="text-xs italic text-muted-foreground">"{greeting}"</p>
        </div>

        <div className="rounded-lg border p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
              <PhoneOutgoing className="h-4 w-4 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium">{businessName} — Intake Specialist</p>
              <Badge variant="secondary" className="text-[10px]">Outbound</Badge>
            </div>
          </div>
          <p className="text-xs italic text-muted-foreground">
            "Hey, is this [caller name]? This is {agentName} with {businessName}..."
          </p>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {error}
        </div>
      )}

      <Button className="w-full" size="lg" onClick={onCreate} disabled={creating}>
        {creating ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Creating your AI team...
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4 mr-2" />
            Create AI Team
          </>
        )}
      </Button>
    </div>
  )
}
